import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	ChevronUp,
	MessageSquare,
	GitFork,
	Clock,
	Star,
} from "lucide-react";
import type { Idea, User } from "@/lib/types";

interface IdeaCardProps {
	idea: Idea;
	author: User | undefined;
	currentUserId: string;
	rank?: number;
	onVote: (ideaId: string) => void;
	onClick: (ideaId: string) => void;
}

function timeAgo(dateStr: string): string {
	const seconds = Math.floor(
		(Date.now() - new Date(dateStr).getTime()) / 1000,
	);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d ago`;
	const weeks = Math.floor(days / 7);
	return `${weeks}w ago`;
}

function wordCount(text: string): number {
	return text.trim().split(/\s+/).length;
}

export function IdeaCard({
	idea,
	author,
	currentUserId,
	rank,
	onVote,
	onClick,
}: IdeaCardProps) {
	const hasVoted = idea.votedBy.includes(currentUserId);
	const totalWords =
		wordCount(idea.problem) +
		wordCount(idea.solution) +
		wordCount(idea.targetMarket) +
		wordCount(idea.monetization);

	return (
		<div
			className="group flex gap-4 p-5 rounded-xl border border-border bg-card hover:border-orange-200 hover:shadow-md transition-all duration-200 cursor-pointer"
			onClick={() => onClick(idea.id)}
		>
			{/* Vote button */}
			<div className="flex flex-col items-center shrink-0">
				{rank && (
					<span className="text-xs font-bold text-muted-foreground mb-2">
						#{rank}
					</span>
				)}
				<button
					onClick={(e) => {
						e.stopPropagation();
						onVote(idea.id);
					}}
					className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg border-2 transition-all duration-200 ${
						hasVoted
							? "border-orange-500 bg-orange-50 text-orange-600"
							: "border-border hover:border-orange-300 text-muted-foreground hover:text-orange-500"
					}`}
				>
					<ChevronUp
						className={`w-5 h-5 ${hasVoted ? "text-orange-500" : ""}`}
					/>
					<span className="text-sm font-bold">{idea.votes}</span>
				</button>
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex items-start justify-between gap-3 mb-2">
					<h3 className="text-base font-semibold leading-snug group-hover:text-orange-600 transition-colors line-clamp-2">
						{idea.title}
					</h3>
				</div>

				<p className="text-sm text-muted-foreground line-clamp-2 mb-3 leading-relaxed">
					{idea.problem}
				</p>

				{/* Tags */}
				<div className="flex flex-wrap gap-1.5 mb-3">
					{idea.tags.map((tag) => (
						<Badge
							key={tag}
							variant="secondary"
							className="text-[11px] px-2 py-0 h-5 font-medium"
						>
							{tag}
						</Badge>
					))}
				</div>

				{/* Meta row */}
				<div className="flex items-center gap-4 text-xs text-muted-foreground">
					<div className="flex items-center gap-1.5">
						<Avatar className="w-5 h-5">
							<AvatarFallback className="text-[9px] bg-muted">
								{author?.avatar ?? "?"}
							</AvatarFallback>
						</Avatar>
						<span className="font-medium text-foreground">
							{author?.name ?? "Unknown"}
						</span>
						{author && (
							<span className="flex items-center text-amber-500">
								<Star className="w-3 h-3 mr-0.5" />
								{author.reputation}
							</span>
						)}
					</div>

					<span className="flex items-center gap-1">
						<MessageSquare className="w-3.5 h-3.5" />
						{idea.commentCount}
					</span>

					{idea.forkedFromId && (
						<span className="flex items-center gap-1 text-blue-500">
							<GitFork className="w-3.5 h-3.5" />
							Fork
						</span>
					)}

					{idea.currentVersion > 1 && (
						<span className="text-emerald-600">v{idea.currentVersion}</span>
					)}

					<span className="flex items-center gap-1">
						<Clock className="w-3.5 h-3.5" />
						{timeAgo(idea.createdAt)}
					</span>

					<span className="ml-auto text-[11px]">{totalWords} words</span>
				</div>
			</div>
		</div>
	);
}
